import { Component, Signal, inject, input, model, output } from "@angular/core";
import { MergePatientsDataService } from "./services/merge-patients-data.service";
import { DialogModule } from "primeng/dialog";
import { MergePatientsSelectionService } from "./services/merge-patients-selection.service";
import { MergePatientsSelectionComponent } from "./merge-patients-selection.component";
import { MergeProcessingService } from "./services/merge-processing.service";
import { MergePatientAction, MergePatientsAction } from "./services/merge-patients-action";
import { MergePatient } from "./merge-patient";
import { PrimeTemplate } from "primeng/api";
import { ButtonModule } from "primeng/button";
import { MergePatientsProcessingComponent } from "./processing/merge-patients-processing.component";
import { toObservable } from "@angular/core/rxjs-interop";
import { combineLatest, filter, switchMap, tap } from "rxjs";
import { OverlayPanelModule } from "primeng/overlaypanel";

@Component({
    selector: "merge-patient-dialog",
    standalone: true,
    imports: [
        DialogModule, 
        MergePatientsSelectionComponent, 
        PrimeTemplate, 
        ButtonModule, 
        OverlayPanelModule,
        MergePatientsProcessingComponent
    ],
    providers: [
        MergePatientsDataService,
        MergePatientsSelectionService,
        MergeProcessingService,
        {
            provide: MergePatientAction,
            useExisting: MergePatientDialogComponent
        }
    ],
    templateUrl: "./merge-patient.dialog.component.html"
})
export class MergePatientDialogComponent implements MergePatientsAction {
    readonly visible = model(false);
    readonly parentId = input.required<string>();
    readonly childId = input.required<string>();
    readonly authToken = input<string>();

    readonly mergeConfirmed = output<string>();
    readonly onHide = output<void>();

    private readonly dataService = inject(MergePatientsDataService);
    private readonly selectionService = inject(MergePatientsSelectionService);
    protected readonly processingService = inject(MergeProcessingService);

    private parentLoader$ = this.createLoader(this.parentId);
    private childLoader$ = this.createLoader(this.childId);
    private loader$ = combineLatest([this.parentLoader$, this.childLoader$]).pipe(
        tap(([parent, child]) => {
            this.selectionService.clearSelection();
            this.selectionService.select(parent);
            this.selectionService.select(child);
        })
    );

    constructor() {
        this.loader$.subscribe();
    }

    private createLoader(patientId: Signal<string>) {
        return toObservable(patientId).pipe(
            filter(id => !!id),
            switchMap(id => this.dataService.getPatient(id))
        );
    }

    merge(parent: MergePatient, child: MergePatient) {
        this.mergeConfirmed.emit(parent.id);
        this.processingService.start(parent.id, child.id, this.authToken());
    }
}
